// framework
import { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { CompositeFilterDescriptor, State } from "@progress/kendo-data-query";
// other
import { getServicesUri } from "../../api/uriHelper";
import { LinkIcon } from "@primer/octicons-react";
import SimpleAlert from "../simpleAlerts/SimpleAlertView";
import { ICustomFilterExpression } from "./ODataReader";
import * as Helper from "./helper";

interface IProps {
    title: string;
    url: string | undefined;
    dataState?: State | undefined;
    additionalFilters?: CompositeFilterDescriptor | undefined; // for additional filter expressions, which are injected, that the user cannot control
    customFilterExpressions?: Array<ICustomFilterExpression> | undefined; // for custom expressions filter expressions
    additionalQueries?: string[] | undefined; // for additional odata query to add to the string (i.e. $expand). not great for $filter as the additional expressions may clash.
}

export default function ODataCopyLinkButton(props: IProps) {
    const [showDialog, setShowDialog] = useState(false);
    const [showCopied, setShowCopied] = useState(false);
    let isDynamic = false;

    useEffect(() => {
        setShowCopied(false);
    }, [showDialog]);

    // we can choose to use the URL that comes in
    // if we attach dataState then we want to transform the URL dynamically based on the query parameters
    // also ensure the url is an absolute url
    let url: string | undefined;
    if (!props.url) {
        url = undefined;
    } else if (!props.dataState) {
        url = isAbsoluteUrl(props.url!) ? props.url! : getServicesUri() + props.url;
    } else {
        const isValid = Helper.getDataStateFilterValidationMessages(props.dataState.filter) === undefined;
        url = isValid ? Helper.getUrl(props.url!, props.dataState!, props.additionalQueries, props.customFilterExpressions, props.additionalFilters, true) : undefined;
        isDynamic = true;
    }

    function copyLinkDialog() {
        return (
            <Dialog title="Copy Link" onClose={() => setShowDialog(false)} width={600}>
                {showCopied && <SimpleAlert message="The link has been copied to the clipboard." alertType="success" dismissable={false} />}
                {isDynamic && (
                    <span>
                        <p>This link will return the full set of data based on your search parameters.</p>
                        <p>Please note this link is temporary and the data returned is subject to change.</p>
                    </span>
                )}
                {!isDynamic && (
                    <span>
                        <p>This link will return the full set of data.</p>
                        <p>Please note the data returned is subject to change.</p>
                    </span>
                )}
                <span aria-label="Link text">
                    <textarea rows={3} className="form-control" value={url} readOnly style={{ wordBreak: "break-all" }}></textarea>
                </span>
                <DialogActionsBar>
                    <span className="mx-3 my-2">
                        <button className="btn btn-outline-primary me-2" onClick={copyLinkTextToClipboard} disabled={!url}>
                            Copy Link to Clipboard
                        </button>
                        <button className="btn btn-outline-primary" onClick={() => setShowDialog(false)}>
                            Close
                        </button>
                    </span>
                </DialogActionsBar>
            </Dialog>
        );
    }

    function copyLinkTextToClipboard() {
        navigator.clipboard.writeText(url!).then(() => setShowCopied(true));
    }

    return (
        <>
            <button className="btn btn-outline-secondary me-2" title={`Get the ${props.title} OData link.`} onClick={() => setShowDialog(true)} disabled={!url}>
                <span>
                    <span>
                        {props.title}
                        {` OData`}
                    </span>
                    <LinkIcon className="ms-1" size={12} verticalAlign="text-top" aria-label="Get the OData link." />
                </span>
            </button>
            {showDialog && copyLinkDialog()}
        </>
    );
}

function isAbsoluteUrl(url: string): boolean {
    try {
        new URL(url);
        return true;
    } catch (_) {
        return false;
    }
}
